import React, { useEffect, useState } from "react";
import {
  Card,
  Space,
  Row,
  Col,
  notification,
  Table,
  TableColumnsType,
  Typography,
  Tag,
  Button,
  Modal,
  message,
} from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { EvaluationService } from "../../services/EvaluationService";
import { usePermissions } from "../../PermissionsProvider";
import { EvaluationRun } from "../../model_gen/evaluation";
import moment from "moment";
import { EvaluateRequest, EvaluateResult } from "../../model_gen/chat";
import { SearchOutlined, CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { Sentinel } from "../../model_gen/sentinel";
import EditSentinelForm from "../Sentinel/EditSentinelForm";

const { Title, Text } = Typography;

const EvaluationDetail: React.FC = () => {
  const { evaluationId: evaluationRunId } = useParams();
  const { apiKey } = usePermissions();
  const [evaluationRun, setEvaluationRun] = useState<EvaluationRun | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [isEditSentinelModalVisible, setIsEditSentinelModalVisible] =
    useState(false);
  const [selectedSentinel, setSelectedSentinel] = useState<Sentinel | null>(
    null
  );
  const [requests, setRequests] = useState<EvaluateRequest[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, EvaluateRequest[]>
  >({});
  const navigate = useNavigate();

  useEffect(() => {
    if (evaluationRunId) {
      setIsLoading(true);
      EvaluationService.getEvaluationRun(
        parseInt(evaluationRunId),
        setIsLoading,
        apiKey
      ).subscribe(
        (data: EvaluationRun) => {
          setEvaluationRun(data);
          setIsLoading(false);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch evaluation details: ${error}`,
          });
          setIsLoading(false);
        }
      );
    }
  }, [evaluationRunId, apiKey]);

  useEffect(() => {
    if (evaluationRunId) {
      if (!requestsByPage[currentPage]) {
        fetchRequests(currentPage);
      } else {
        setRequests(requestsByPage[currentPage]);
      }
    }
  }, [evaluationRunId, currentPage, requestsByPage]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * 10;
    setIsRequestsLoading(true);
    EvaluationService.getEvaluationRunRequests(
      parseInt(evaluationRunId),
      offset,
      10,
      setIsRequestsLoading,
      apiKey
    ).subscribe(
      (data: EvaluateRequest[]) => {
        setRequests(data);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: data,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  const handleRowClick = (record: EvaluateRequest) => {
    const applicationId = evaluationRun?.application?.id;
    const sessionId = record.chat_completion_request?.session_id;
    const requestId = record.chat_completion_request?.request_id;

    if (applicationId && sessionId && requestId) {
      navigate(
        `/application/${applicationId}/session/${encodeURIComponent(
          sessionId
        )}/request/${requestId}`
      );
    }
  };

  const requestColumns: TableColumnsType<EvaluateRequest> = [
    {
      title: "Date Created",
      dataIndex: ["chat_completion_request", "date_created"],
      key: "date_created",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: "Request ID",
      key: "request_id",
      align: "right",
      render: (_, request: EvaluateRequest) => {
        const requestId = request.chat_completion_request?.request_id || "";
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            {requestId.slice(-8)}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                message.success("Request ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "Evaluation ID",
      key: "evaluation_request_id",
      align: "right",
      render: (_, request: EvaluateRequest) => {
        const evaluationId = request.evaluation_request_id || "";
        return (
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            {evaluationId.slice(-8)}
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(evaluationId);
                message.success("Evaluation ID copied to clipboard");
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      align: "right",
      render: (_, request: EvaluateRequest) => {
        const hasFault = request.eval_results_set?.evaluation_results?.some(
          (result: EvaluateResult) => result.status === "FAULT"
        );

        return (
          <Tag color={hasFault ? "error" : "success"}>
            {hasFault ? "FAIL" : "PASS"}
          </Tag>
        );
      },
    },
  ];

  const handleViewSentinel = (sentinel: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedSentinel(sentinel);
    setIsEditSentinelModalVisible(true);
  };

  const renderSentinelTag = (sentinel: any) => {
    return (
      <Tag key={sentinel.id} color="blue" style={{ marginRight: 3 }}>
        <span style={{ marginRight: 8 }}>{sentinel.sentinel_name}</span>
        <Button
          icon={<SearchOutlined />}
          size="small"
          type="text"
          onClick={(e) => handleViewSentinel(sentinel, e)}
          style={{ cursor: "pointer" }}
        />
      </Tag>
    );
  };

  return (
    <Card className="evaluation-detail-wrapper" loading={isLoading}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}>
          <Title level={3}>Evaluation Run Details</Title>
        </div>

        {evaluationRun && (
          <>
            <div>
              <Card
                size="small"
                title={
                  <Text strong style={{ fontSize: "16px" }}>
                    Configuration
                  </Text>
                }
                style={{
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  marginTop: "16px",
                }}>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Text type="secondary">Application</Text>
                    <div>
                      <Text strong style={{ fontSize: "14px" }}>
                        {evaluationRun.application?.application_name ||
                          "Not available"}
                      </Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">Intent</Text>
                    <div>
                      <Tag color="blue">
                        {evaluationRun.intent?.action_type || "Not available"}
                      </Tag>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">Date Range</Text>
                    <div>
                      <Text strong style={{ fontSize: "14px" }}>
                        {`${moment(evaluationRun.date_start * 1000).format(
                          "MMM D, YYYY"
                        )} to ${moment(evaluationRun.date_end * 1000).format(
                          "MMM D, YYYY"
                        )}`}
                      </Text>
                    </div>
                  </Col>
                  <Col span={16}>
                    <Text type="secondary">Selected Sentinels</Text>
                    <div>
                      {evaluationRun.sentinels.length > 0 ? (
                        <Space wrap size="small">
                          {evaluationRun.sentinels.map((sentinel) =>
                            renderSentinelTag(sentinel)
                          )}
                        </Space>
                      ) : (
                        "No sentinels selected"
                      )}
                    </div>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">Date Created</Text>
                    <div>
                      <Text strong style={{ fontSize: "14px" }}>
                        {moment(
                          Number(evaluationRun.date_created * 1000)
                        ).format("MMM D, YYYY HH:mm:ss")}
                      </Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>

            <div>
              <Card
                size="small"
                title={
                  <Text strong style={{ fontSize: "16px" }}>
                    Results
                  </Text>
                }
                style={{
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  marginTop: "16px",
                }}>
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <Text type="secondary">Total Requests</Text>
                    <div>
                      <Text strong style={{ fontSize: "14px" }}>
                        {(evaluationRun.meta?.total_count as number) || 0}
                      </Text>
                    </div>
                  </Col>
                  <Col span={6}>
                    <Text type="secondary">Pass</Text>
                    <div>
                      <Text
                        strong
                        style={{ fontSize: "14px", color: "#52c41a" }}>
                        {(evaluationRun.meta?.pass_count as number) || 0}
                      </Text>
                    </div>
                  </Col>
                  <Col span={6}>
                    <Text type="secondary">Fail</Text>
                    <div>
                      <Text
                        strong
                        style={{ fontSize: "14px", color: "#ff4d4f" }}>
                        {(evaluationRun.meta?.fault_count as number) || 0}
                      </Text>
                    </div>
                  </Col>
                  <Col span={6}>
                    <Text type="secondary">Pass Rate</Text>
                    <div>
                      <Text strong style={{ fontSize: "14px" }}>
                        {typeof evaluationRun.meta?.pass_rate === "number"
                          ? `${(evaluationRun.meta.pass_rate * 100).toFixed(
                              2
                            )}%`
                          : "0%"}
                      </Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>

            <div>
              <Text style={{ color: "#828282", fontSize: 16, fontWeight: 600 }}>
                Requests
              </Text>
              <Table
                rowKey="evaluation_request_id"
                columns={requestColumns}
                dataSource={requests}
                loading={isRequestsLoading}
                onRow={(record) => ({
                  onClick: () => handleRowClick(record),
                  style: { cursor: "pointer" },
                })}
                pagination={{
                  current: currentPage,
                  pageSize: 10,
                  total: Number(evaluationRun?.meta?.total_count) || 0,
                  onChange: (page) => setCurrentPage(page),
                }}
                style={{ marginTop: "16px" }}
              />
            </div>
          </>
        )}
      </Space>

      <Modal
        visible={isEditSentinelModalVisible}
        onCancel={() => {
          setIsEditSentinelModalVisible(false);
          setSelectedSentinel(null);
        }}
        footer={null}
        width={800}
        title="Sentinel"
        closeIcon={<CloseOutlined />}>
        {selectedSentinel && (
          <EditSentinelForm
            key={selectedSentinel.id}
            sentinel={selectedSentinel}
            loading={false}
            isDataLoaded={true}
            onSubmit={() => {}} // Read-only mode
            onClose={() => {
              setIsEditSentinelModalVisible(false);
              setSelectedSentinel(null);
            }}
            readOnly={true}
          />
        )}
      </Modal>
    </Card>
  );
};

export default EvaluationDetail;
